/**
 * FristManager - Fullverdig frist-håndtering
 * Erstatter FristerPlaceholder med ekte funksjonalitet
 * @version 1.0.0 - MASTERPROMPT v2.0 compliant
 * 
 * Features:
 * - Legg til, rediger og slett frister
 * - Visuell statusindikator (kritisk, snart, normal)
 * - Kalendereksport (ICS)
 * - Modulspesifikke frist-maler
 * - Lokal lagring (privacy-first)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Calendar, Plus, Trash2, Edit2, Download, AlertTriangle,
  Clock, CheckCircle, X, Bell, Filter, SortAsc, FileText,
  ChevronDown, ChevronUp, CalendarDays, AlertCircle
} from 'lucide-react';
import { 
  getActiveDeadlinesCount, 
  formatDate, 
  getDaysUntil, 
  getDeadlineStatus 
} from '../utils/deadlineUtils';

// Predefinerte frist-maler per modul
const FRIST_TEMPLATES = {
  arbeidsrett: [
    { title: 'Oppsigelsestid arbeidstaker', days: 30, category: 'oppsigelse', description: 'Standard oppsigelsestid ved egen oppsigelse (AML § 15-3)' },
    { title: 'Oppsigelsestid arbeidsgiver', days: 30, category: 'oppsigelse', description: 'Standard oppsigelsestid fra arbeidsgiver (AML § 15-3)' },
    { title: 'Permitteringsvarsel', days: 14, category: 'permittering', description: 'Minimum varslingsfrist for permittering (AML § 15-3)' },
    { title: 'Klage på oppsigelse', days: 14, category: 'klage', description: 'Frist for å kreve forhandlinger (AML § 17-3)' },
    { title: 'Søksmålsfrist oppsigelse', days: 56, category: 'søksmål', description: '8 uker fra forhandlinger (AML § 17-4)' },
    { title: 'Feriepenger utbetaling', days: 0, category: 'utbetaling', description: 'Siste ordinære lønningsdag før ferien (Ferieloven § 11)' }
  ],
  husleierett: [
    { title: 'Oppsigelsestid leietaker', days: 90, category: 'oppsigelse', description: '3 måneder oppsigelsestid (Husleieloven § 9-6)' },
    { title: 'Oppsigelsestid utleier', days: 90, category: 'oppsigelse', description: '3 måneder oppsigelsestid (Husleieloven § 9-6)' },
    { title: 'Protest på oppsigelse', days: 30, category: 'klage', description: '1 måned til å protestere skriftlig (Husleieloven § 9-8)' },
    { title: 'Reklamasjon mangel', days: 14, category: 'reklamasjon', description: 'Rimelig tid etter mangel oppdaget (Husleieloven § 2-8)' },
    { title: 'Depositum tilbakebetaling', days: 30, category: 'depositum', description: '1 måned etter leieforhold avsluttet (Husleieloven § 3-5)' },
    { title: 'Husleieøkning varsel', days: 30, category: 'husleie', description: 'Minimum 1 måneds varsel (Husleieloven § 4-2)' }
  ],
  forbrukerrett: [
    { title: 'Angrefrist', days: 14, category: 'angrerett', description: '14 dagers angrerett (Angrerettloven § 20)' },
    { title: 'Reklamasjon mangel', days: 60, category: 'reklamasjon', description: 'Innen rimelig tid etter mangel oppdaget (Forbrukerkjøpsloven § 27)' },
    { title: 'Absolutt reklamasjonsfrist', days: 730, category: 'reklamasjon', description: '2 år for de fleste varer (Forbrukerkjøpsloven § 27)' },
    { title: 'Reklamasjon varige varer', days: 1825, category: 'reklamasjon', description: '5 år for varige forbruksvarer (Forbrukerkjøpsloven § 27)' },
    { title: 'Svar fra selger', days: 14, category: 'svar', description: 'Rimelig tid for selger å svare på reklamasjon' },
    { title: 'FKU klagefrist', days: 90, category: 'klage', description: '3 måneder fra selgers endelige avslag' }
  ],
  barnevern: [
    { title: 'Klage på vedtak', days: 21, category: 'klage', description: '3 uker klagefrist (Forvaltningsloven § 29)' },
    { title: 'Klage til Statsforvalteren', days: 21, category: 'klage', description: '3 uker til overordnet organ' },
    { title: 'Begjæring om rettslig prøving', days: 30, category: 'søksmål', description: '1 måned etter vedtak (Barnevernsloven § 7-24)' },
    { title: 'Partsinnsyn svar', days: 21, category: 'innsyn', description: '3 uker for å gi innsyn (Forvaltningsloven § 11a)' },
    { title: 'Akuttvedtak varighet', days: 42, category: 'akutt', description: '6 uker maks for akuttvedtak (Barnevernsloven § 4-2)' }
  ]
};

// Frist-kategorier med farger og ikoner
const CATEGORIES = {
  oppsigelse: { label: 'Oppsigelse', color: 'text-red-400', bg: 'bg-red-500/10' },
  permittering: { label: 'Permittering', color: 'text-orange-400', bg: 'bg-orange-500/10' },
  klage: { label: 'Klage', color: 'text-purple-400', bg: 'bg-purple-500/10' },
  søksmål: { label: 'Søksmål', color: 'text-red-500', bg: 'bg-red-500/10' },
  reklamasjon: { label: 'Reklamasjon', color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
  angrerett: { label: 'Angrerett', color: 'text-blue-400', bg: 'bg-blue-500/10' },
  depositum: { label: 'Depositum', color: 'text-green-400', bg: 'bg-green-500/10' },
  husleie: { label: 'Husleie', color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
  svar: { label: 'Svar', color: 'text-indigo-400', bg: 'bg-indigo-500/10' },
  innsyn: { label: 'Innsyn', color: 'text-teal-400', bg: 'bg-teal-500/10' },
  akutt: { label: 'Akutt', color: 'text-red-500', bg: 'bg-red-500/20' },
  utbetaling: { label: 'Utbetaling', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  annet: { label: 'Annet', color: 'text-gray-400', bg: 'bg-gray-500/10' }
};

// Status-konfigurasjoner
const STATUS_CONFIG = {
  expired: { 
    label: 'Utløpt', 
    color: 'text-red-400', 
    bg: 'bg-red-500/20', 
    border: 'border-red-500/50',
    icon: AlertCircle 
  },
  urgent: { 
    label: 'Kritisk', 
    color: 'text-orange-400', 
    bg: 'bg-orange-500/20', 
    border: 'border-orange-500/50',
    icon: AlertTriangle 
  },
  warning: { 
    label: 'Snart', 
    color: 'text-yellow-400', 
    bg: 'bg-yellow-500/20', 
    border: 'border-yellow-500/50',
    icon: Clock 
  },
  normal: { 
    label: 'OK', 
    color: 'text-green-400', 
    bg: 'bg-green-500/20', 
    border: 'border-green-500/50',
    icon: CheckCircle 
  },
  completed: { 
    label: 'Fullført', 
    color: 'text-gray-400', 
    bg: 'bg-gray-500/20', 
    border: 'border-gray-500/50',
    icon: CheckCircle 
  }
};

// Generer ICS kalender-fil
const generateICS = (frist) => {
  const startDate = new Date(frist.date);
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + 1);
  
  const formatICSDate = (date) => {
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };
  
  const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//RettVerk//Frist-håndtering//NO
BEGIN:VEVENT
UID:${frist.id}@rettwerk.no
DTSTAMP:${formatICSDate(new Date())}
DTSTART;VALUE=DATE:${startDate.toISOString().split('T')[0].replace(/-/g, '')}
DTEND;VALUE=DATE:${endDate.toISOString().split('T')[0].replace(/-/g, '')}
SUMMARY:⚖️ ${frist.title}
DESCRIPTION:${frist.description || 'Juridisk frist fra RettVerk'}\\n\\nKategori: ${CATEGORIES[frist.category]?.label || frist.category}
LOCATION:RettVerk
BEGIN:VALARM
ACTION:DISPLAY
DESCRIPTION:Påminnelse: ${frist.title}
TRIGGER:-P1D
END:VALARM
BEGIN:VALARM
ACTION:DISPLAY
DESCRIPTION:Påminnelse: ${frist.title} (3 dager)
TRIGGER:-P3D
END:VALARM
BEGIN:VALARM
ACTION:DISPLAY
DESCRIPTION:Påminnelse: ${frist.title} (1 uke)
TRIGGER:-P7D
END:VALARM
END:VEVENT
END:VCALENDAR`;
  
  return icsContent;
};

// Download ICS-fil
const downloadICS = (frist) => {
  const icsContent = generateICS(frist);
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `frist_${frist.title.toLowerCase().replace(/\s+/g, '_')}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Eksporter alle frister til én ICS-fil
const downloadAllICS = (frister, moduleId) => {
  const events = frister.map(frist => {
    const startDate = new Date(frist.date);
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 1);
    
    const formatICSDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };
    
    return `BEGIN:VEVENT
UID:${frist.id}@rettwerk.no
DTSTAMP:${formatICSDate(new Date())}
DTSTART;VALUE=DATE:${startDate.toISOString().split('T')[0].replace(/-/g, '')}
DTEND;VALUE=DATE:${endDate.toISOString().split('T')[0].replace(/-/g, '')}
SUMMARY:⚖️ ${frist.title}
DESCRIPTION:${frist.description || 'Juridisk frist fra RettVerk'}
BEGIN:VALARM
ACTION:DISPLAY
DESCRIPTION:Påminnelse: ${frist.title}
TRIGGER:-P1D
END:VALARM
END:VEVENT`;
  }).join('\n');
  
  const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//RettVerk//Frist-håndtering//NO
X-WR-CALNAME:RettVerk Frister
${events}
END:VCALENDAR`;
  
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `rettwerk_frister_${moduleId}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export function FristManager({ moduleId, moduleTitle = 'saker' }) {
  const [frister, setFrister] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingFrist, setEditingFrist] = useState(null);
  const [filter, setFilter] = useState('all'); // all, active, expired, completed
  const [sortBy, setSortBy] = useState('date'); // date, status, category
  const [showTemplates, setShowTemplates] = useState(false);
  
  // Last inn frister fra localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(`${moduleId}_frister`);
      if (stored) {
        setFrister(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Kunne ikke laste frister:', error);
    }
  }, [moduleId]);
  
  // Lagre frister til localStorage
  const saveFrister = useCallback((newFrister) => {
    try {
      localStorage.setItem(`${moduleId}_frister`, JSON.stringify(newFrister));
      setFrister(newFrister);
    } catch (error) {
      console.error('Kunne ikke lagre frister:', error);
    }
  }, [moduleId]);
  
  // Legg til ny frist
  const addFrist = useCallback((fristData) => {
    const newFrist = {
      id: `frist_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      ...fristData,
      createdAt: new Date().toISOString(),
      completed: false
    };
    saveFrister([...frister, newFrist]);
    setShowForm(false);
  }, [frister, saveFrister]);
  
  // Oppdater frist
  const updateFrist = useCallback((id, updates) => {
    const updated = frister.map(f => 
      f.id === id ? { ...f, ...updates } : f
    );
    saveFrister(updated);
    setEditingFrist(null);
  }, [frister, saveFrister]);
  
  // Slett frist
  const deleteFrist = useCallback((id) => {
    if (confirm('Er du sikker på at du vil slette denne fristen?')) {
      saveFrister(frister.filter(f => f.id !== id));
    }
  }, [frister, saveFrister]);
  
  // Marker som fullført
  const toggleComplete = useCallback((id) => {
    const updated = frister.map(f => 
      f.id === id ? { 
        ...f, 
        completed: !f.completed,
        completedAt: !f.completed ? new Date().toISOString() : null
      } : f
    );
    saveFrister(updated);
  }, [frister, saveFrister]);
  
  // Legg til fra mal
  const addFromTemplate = useCallback((template) => {
    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() + template.days);
    
    addFrist({
      title: template.title,
      description: template.description,
      date: targetDate.toISOString().split('T')[0],
      category: template.category
    });
    setShowTemplates(false);
  }, [addFrist]);
  
  // Filtrer og sorter frister
  const filteredFrister = frister
    .filter(f => {
      if (filter === 'all') return true;
      if (filter === 'completed') return f.completed;
      if (filter === 'expired') {
        const daysUntil = getDaysUntil(f.date);
        return daysUntil < 0 && !f.completed;
      }
      if (filter === 'active') return !f.completed && getDaysUntil(f.date) >= 0;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'date') {
        return new Date(a.date) - new Date(b.date);
      }
      if (sortBy === 'status') {
        const statusOrder = { expired: 0, urgent: 1, warning: 2, normal: 3, completed: 4 };
        const statusA = a.completed ? 'completed' : getDeadlineStatus(getDaysUntil(a.date));
        const statusB = b.completed ? 'completed' : getDeadlineStatus(getDaysUntil(b.date));
        return statusOrder[statusA] - statusOrder[statusB];
      }
      if (sortBy === 'category') {
        return (a.category || '').localeCompare(b.category || '');
      }
      return 0;
    });
  
  // Statistikk
  const stats = {
    total: frister.length,
    active: frister.filter(f => !f.completed && getDaysUntil(f.date) >= 0).length,
    urgent: frister.filter(f => !f.completed && getDaysUntil(f.date) <= 7 && getDaysUntil(f.date) >= 0).length,
    expired: frister.filter(f => !f.completed && getDaysUntil(f.date) < 0).length,
    completed: frister.filter(f => f.completed).length
  };
  
  const templates = FRIST_TEMPLATES[moduleId] || [];
  
  return (
    <div className="space-y-6" data-testid="frist-manager">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-[var(--text-main)]" style={{ fontFamily: 'Playfair Display, serif' }}>
            Frist-håndtering
          </h2>
          <p className="text-sm text-[var(--text-muted)]">
            Hold oversikt over viktige juridiske frister i {moduleTitle}
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {frister.length > 0 && (
            <button
              onClick={() => downloadAllICS(frister.filter(f => !f.completed), moduleId)}
              className="flex items-center gap-2 px-3 py-2 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg text-sm text-[var(--text-muted)] hover:text-[var(--text-main)] hover:border-[var(--copper)]/40 transition-all"
              title="Eksporter alle til kalender"
              data-testid="export-all-frister"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Eksporter</span>
            </button>
          )}
          
          {templates.length > 0 && (
            <button
              onClick={() => setShowTemplates(!showTemplates)}
              className="flex items-center gap-2 px-3 py-2 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg text-sm text-[var(--text-muted)] hover:text-[var(--text-main)] hover:border-[var(--copper)]/40 transition-all"
              data-testid="show-templates"
            >
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Maler</span>
              {showTemplates ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          )}
          
          <button
            onClick={() => { setShowForm(true); setEditingFrist(null); }}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--copper)] text-white rounded-lg hover:opacity-90 transition-opacity"
            data-testid="add-frist-button"
          >
            <Plus className="w-4 h-4" />
            <span>Ny frist</span>
          </button>
        </div>
      </div>
      
      {/* Statistikk-kort */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <StatCard 
          label="Totalt" 
          value={stats.total} 
          color="text-[var(--text-main)]" 
          bg="bg-[var(--bg-surface)]"
        />
        <StatCard 
          label="Aktive" 
          value={stats.active} 
          color="text-blue-400" 
          bg="bg-blue-500/10"
        />
        <StatCard 
          label="Kritiske" 
          value={stats.urgent} 
          color="text-orange-400" 
          bg="bg-orange-500/10"
          pulse={stats.urgent > 0}
        />
        <StatCard 
          label="Utløpt" 
          value={stats.expired} 
          color="text-red-400" 
          bg="bg-red-500/10"
          pulse={stats.expired > 0}
        />
        <StatCard 
          label="Fullført" 
          value={stats.completed} 
          color="text-green-400" 
          bg="bg-green-500/10"
        />
      </div>
      
      {/* Frist-maler dropdown */}
      {showTemplates && templates.length > 0 && (
        <div className="p-4 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-xl">
          <h3 className="text-sm font-medium text-[var(--text-main)] mb-3">
            Juridiske frist-maler
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {templates.map((template, idx) => (
              <button
                key={idx}
                onClick={() => addFromTemplate(template)}
                className="flex items-start gap-3 p-3 bg-[var(--bg-highlight)] border border-[var(--border-subtle)] rounded-lg text-left hover:border-[var(--copper)]/40 transition-all group"
                data-testid={`template-${idx}`}
              >
                <div className={`w-8 h-8 rounded-lg ${CATEGORIES[template.category]?.bg || 'bg-gray-500/10'} flex items-center justify-center flex-shrink-0`}>
                  <Calendar className={`w-4 h-4 ${CATEGORIES[template.category]?.color || 'text-gray-400'}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-[var(--text-main)] truncate">
                    {template.title}
                  </p>
                  <p className="text-xs text-[var(--text-muted)]">
                    +{template.days} dager
                  </p>
                </div>
                <Plus className="w-4 h-4 text-[var(--copper)] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Filter og sortering */}
      {frister.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 p-1 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg">
            <Filter className="w-4 h-4 text-[var(--text-muted)] ml-2" />
            {['all', 'active', 'urgent', 'expired', 'completed'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f === 'urgent' ? 'active' : f)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                  filter === f 
                    ? 'bg-[var(--copper)] text-white' 
                    : 'text-[var(--text-muted)] hover:text-[var(--text-main)]'
                }`}
              >
                {f === 'all' ? 'Alle' : f === 'active' ? 'Aktive' : f === 'expired' ? 'Utløpt' : 'Fullført'}
              </button>
            ))}
          </div>
          
          <div className="flex items-center gap-1 p-1 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg">
            <SortAsc className="w-4 h-4 text-[var(--text-muted)] ml-2" />
            {['date', 'status', 'category'].map(s => (
              <button
                key={s}
                onClick={() => setSortBy(s)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                  sortBy === s 
                    ? 'bg-[var(--copper)] text-white' 
                    : 'text-[var(--text-muted)] hover:text-[var(--text-main)]'
                }`}
              >
                {s === 'date' ? 'Dato' : s === 'status' ? 'Status' : 'Kategori'}
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Frist-liste */}
      {filteredFrister.length === 0 ? (
        <EmptyState 
          hasAnyFrister={frister.length > 0}
          onAddClick={() => setShowForm(true)}
          onTemplateClick={() => setShowTemplates(true)}
          hasTemplates={templates.length > 0}
        />
      ) : (
        <div className="space-y-3">
          {filteredFrister.map(frist => (
            <FristCard
              key={frist.id}
              frist={frist}
              onEdit={() => { setEditingFrist(frist); setShowForm(true); }}
              onDelete={() => deleteFrist(frist.id)}
              onToggleComplete={() => toggleComplete(frist.id)}
              onDownload={() => downloadICS(frist)}
            />
          ))}
        </div>
      )}
      
      {/* Legg til / Rediger modal */}
      {showForm && (
        <FristForm
          frist={editingFrist}
          onSave={editingFrist ? (data) => updateFrist(editingFrist.id, data) : addFrist}
          onClose={() => { setShowForm(false); setEditingFrist(null); }}
          moduleId={moduleId}
        />
      )}
    </div>
  );
}

// Statistikk-kort komponent
function StatCard({ label, value, color, bg, pulse = false }) {
  return (
    <div className={`p-3 ${bg} border border-[var(--border-subtle)] rounded-lg text-center ${pulse ? 'animate-pulse' : ''}`}>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      <p className="text-xs text-[var(--text-muted)]">{label}</p>
    </div>
  );
}

// Tom tilstand komponent
function EmptyState({ hasAnyFrister, onAddClick, onTemplateClick, hasTemplates }) {
  if (hasAnyFrister) {
    return (
      <div className="text-center py-12 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-xl">
        <Filter className="w-12 h-12 text-[var(--text-muted)] mx-auto mb-4 opacity-50" />
        <h3 className="text-lg font-medium text-[var(--text-main)] mb-2">
          Ingen frister matcher filteret
        </h3>
        <p className="text-[var(--text-muted)]">
          Prøv å endre filter for å se andre frister
        </p>
      </div>
    );
  }
  
  return (
    <div className="text-center py-16 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-xl">
      <div className="w-16 h-16 bg-[var(--copper)]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
        <CalendarDays className="w-8 h-8 text-[var(--copper)]" />
      </div>
      <h3 className="text-lg font-semibold text-[var(--text-main)] mb-2">
        Ingen frister registrert
      </h3>
      <p className="text-[var(--text-muted)] mb-6 max-w-md mx-auto">
        Hold oversikt over viktige juridiske frister. Få varsler før frister utløper og eksporter til din kalender.
      </p>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
        <button
          onClick={onAddClick}
          className="flex items-center gap-2 px-6 py-3 bg-[var(--copper)] text-white rounded-lg hover:opacity-90 transition-opacity"
        >
          <Plus className="w-5 h-5" />
          Legg til frist
        </button>
        {hasTemplates && (
          <button
            onClick={onTemplateClick}
            className="flex items-center gap-2 px-6 py-3 bg-[var(--bg-highlight)] border border-[var(--border-subtle)] text-[var(--text-main)] rounded-lg hover:border-[var(--copper)]/40 transition-all"
          >
            <FileText className="w-5 h-5" />
            Bruk mal
          </button>
        )}
      </div>
    </div>
  );
}

// Frist-kort komponent
function FristCard({ frist, onEdit, onDelete, onToggleComplete, onDownload }) {
  const daysUntil = getDaysUntil(frist.date);
  const status = frist.completed ? 'completed' : getDeadlineStatus(daysUntil);
  const statusConfig = STATUS_CONFIG[status];
  const StatusIcon = statusConfig.icon;
  const category = CATEGORIES[frist.category] || CATEGORIES.annet;
  
  return (
    <div 
      className={`p-4 bg-[var(--bg-surface)] border ${statusConfig.border} rounded-xl transition-all hover:shadow-lg ${frist.completed ? 'opacity-60' : ''}`}
      data-testid={`frist-card-${frist.id}`}
    >
      <div className="flex items-start gap-4">
        {/* Status-indikator */}
        <button
          onClick={onToggleComplete}
          className={`w-10 h-10 rounded-lg ${statusConfig.bg} flex items-center justify-center flex-shrink-0 hover:opacity-80 transition-opacity`}
          title={frist.completed ? 'Marker som ikke fullført' : 'Marker som fullført'}
        >
          <StatusIcon className={`w-5 h-5 ${statusConfig.color}`} />
        </button>
        
        {/* Innhold */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className={`font-medium ${frist.completed ? 'line-through text-[var(--text-muted)]' : 'text-[var(--text-main)]'}`}>
                {frist.title}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-xs px-2 py-0.5 rounded-full ${category.bg} ${category.color}`}>
                  {category.label}
                </span>
                <span className={`text-xs ${statusConfig.color}`}>
                  {status === 'expired' 
                    ? `${Math.abs(daysUntil)} dager siden` 
                    : status === 'completed'
                    ? 'Fullført'
                    : daysUntil === 0 
                    ? 'I dag!' 
                    : daysUntil === 1 
                    ? 'I morgen' 
                    : `${daysUntil} dager igjen`
                  }
                </span>
              </div>
            </div>
            
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={onDownload}
                className="p-2 text-[var(--text-muted)] hover:text-[var(--copper)] hover:bg-[var(--bg-highlight)] rounded-lg transition-all"
                title="Last ned til kalender"
              >
                <Download className="w-4 h-4" />
              </button>
              <button
                onClick={onEdit}
                className="p-2 text-[var(--text-muted)] hover:text-[var(--copper)] hover:bg-[var(--bg-highlight)] rounded-lg transition-all"
                title="Rediger"
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                onClick={onDelete}
                className="p-2 text-[var(--text-muted)] hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                title="Slett"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {frist.description && (
            <p className="text-sm text-[var(--text-muted)] mt-2 line-clamp-2">
              {frist.description}
            </p>
          )}
          
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-[var(--border-subtle)]">
            <div className="flex items-center gap-1.5 text-xs text-[var(--text-muted)]">
              <Calendar className="w-3.5 h-3.5" />
              <span>{formatDate(frist.date)}</span>
            </div>
            {frist.completedAt && (
              <div className="flex items-center gap-1.5 text-xs text-green-400">
                <CheckCircle className="w-3.5 h-3.5" />
                <span>Fullført {formatDate(frist.completedAt)}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Frist-skjema komponent
function FristForm({ frist, onSave, onClose, moduleId }) {
  const [formData, setFormData] = useState({
    title: frist?.title || '',
    description: frist?.description || '',
    date: frist?.date || new Date().toISOString().split('T')[0],
    category: frist?.category || 'annet'
  });
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title || !formData.date) {
      alert('Vennligst fyll inn tittel og dato');
      return;
    }
    onSave(formData);
  };
  
  // Hent tilgjengelige kategorier for denne modulen
  const moduleTemplates = FRIST_TEMPLATES[moduleId] || [];
  const moduleCategories = [...new Set(moduleTemplates.map(t => t.category))];
  const availableCategories = moduleCategories.length > 0 
    ? moduleCategories.map(c => ({ id: c, ...CATEGORIES[c] }))
    : Object.entries(CATEGORIES).map(([id, cat]) => ({ id, ...cat }));
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg bg-[var(--bg-surface)] rounded-2xl border border-[var(--border-subtle)] shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--border-subtle)]">
          <h3 className="text-lg font-semibold text-[var(--text-main)]">
            {frist ? 'Rediger frist' : 'Ny frist'}
          </h3>
          <button
            onClick={onClose}
            className="p-2 text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-highlight)] rounded-lg transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-main)] mb-1.5">
              Tittel *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="F.eks. Klagefrist oppsigelse"
              className="w-full px-4 py-2.5 bg-[var(--bg-highlight)] border border-[var(--border-subtle)] rounded-lg text-[var(--text-main)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--copper)] transition-colors"
              data-testid="frist-title-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[var(--text-main)] mb-1.5">
              Dato *
            </label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
              className="w-full px-4 py-2.5 bg-[var(--bg-highlight)] border border-[var(--border-subtle)] rounded-lg text-[var(--text-main)] focus:outline-none focus:border-[var(--copper)] transition-colors"
              data-testid="frist-date-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[var(--text-main)] mb-1.5">
              Kategori
            </label>
            <div className="grid grid-cols-3 gap-2">
              {availableCategories.slice(0, 6).map(cat => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, category: cat.id }))}
                  className={`px-3 py-2 text-xs font-medium rounded-lg border transition-all ${
                    formData.category === cat.id
                      ? `${cat.bg} ${cat.color} border-current`
                      : 'bg-[var(--bg-highlight)] border-[var(--border-subtle)] text-[var(--text-muted)] hover:border-[var(--copper)]/40'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[var(--text-main)] mb-1.5">
              Beskrivelse
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Valgfri beskrivelse eller lovhenvisning..."
              rows={3}
              className="w-full px-4 py-2.5 bg-[var(--bg-highlight)] border border-[var(--border-subtle)] rounded-lg text-[var(--text-main)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--copper)] transition-colors resize-none"
              data-testid="frist-description-input"
            />
          </div>
          
          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors"
            >
              Avbryt
            </button>
            <button
              type="submit"
              className="flex items-center gap-2 px-6 py-2 bg-[var(--copper)] text-white rounded-lg hover:opacity-90 transition-opacity"
              data-testid="save-frist-button"
            >
              {frist ? 'Lagre endringer' : 'Legg til frist'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default FristManager;
